import defaults from 'lodash/defaults';
import isNull from 'lodash/isNull';
import type {
	BacklogEpicsStoreInitProps,
	EpicCardType,
} from '@atlassian/jira-backlog-epics-store/src/types.tsx';
import { COLORS } from '@atlassian/jira-issue-epic-color/src/common/types.tsx';
import { PARENT_HIERARCHY_TYPE } from '@atlassian/jira-issue-type-hierarchies/src/index.tsx';
import { DEFAULT_PARENT_ISSUE_TYPE_NAME } from '../../../model/constants.tsx';
import type { IssueParent } from '../../../model/issue-parent/types.tsx';
import type { EpicsLite_boardScope_parents_edges_node as LiteEpic } from '../../../services/queries/epics-lite/__generated_apollo__/EpicsLite.ts';
import type {
	EpicsLiteQueryResponse,
	IssueTypeResponse,
} from '../../../services/queries/epics-lite/types.tsx';

const RANK_DISABLED_ID = '0';
const EMPTY_ISSUE_TYPE = { name: '', iconUrl: '', id: '' };

export const transformLiteCard = (
	{ id, key, summary, color, issueTypeDetails }: LiteEpic,
	defaultIssueType?: IssueTypeResponse,
): EpicCardType => {
	const epicData = {
		id: Number(id),
		key,
		name: summary,
		color: isNull(color) ? undefined : color,
		issueType: defaults(
			{
				name: issueTypeDetails?.name,
				iconUrl: issueTypeDetails?.iconUrl,
				id: issueTypeDetails?.id,
			},
			{
				name: defaultIssueType?.name,
				iconUrl: defaultIssueType?.iconUrl,
				id: defaultIssueType?.id,
			},
			EMPTY_ISSUE_TYPE,
		),
		isLoading: true,
		hasError: false,
	};

	return {
		epic: epicData,
		progress: {
			status: {
				toDo: {
					issueCount: 0,
				},
				inProgress: {
					issueCount: 0,
				},
				done: {
					issueCount: 0,
				},
			},
			totalIssues: 0,
		},
	};
};

export const transformLiteQueryData = (
	data: EpicsLiteQueryResponse | undefined,
	isCompanyManaged: boolean,
	firstLevelHierarchyName: string | null,
): BacklogEpicsStoreInitProps => {
	const globalConfig = data?.globalConfig;
	const board = data?.boardScope?.board;

	let isEpicIssueTypeAvailable = false;
	let epicIssueType: IssueTypeResponse | undefined;
	let epicIssueTypeName: string = DEFAULT_PARENT_ISSUE_TYPE_NAME;

	const cards =
		data?.boardScope?.parents?.edges?.flatMap((edge) =>
			edge?.node ? transformLiteCard(edge.node, globalConfig?.issueParentTypeConfig) : [],
		) ?? [];

	if (isCompanyManaged) {
		isEpicIssueTypeAvailable =
			globalConfig?.issueParentTypeConfig.hierarchyLevelType === PARENT_HIERARCHY_TYPE;
		epicIssueType = globalConfig?.issueParentTypeConfig;
		epicIssueTypeName =
			firstLevelHierarchyName ||
			globalConfig?.issueParentTypeConfig.name ||
			DEFAULT_PARENT_ISSUE_TYPE_NAME;
	} else {
		epicIssueType = data?.boardScope?.projectLocation?.issueTypes.find(
			({ hierarchyLevelType }) => hierarchyLevelType === PARENT_HIERARCHY_TYPE,
		);
		isEpicIssueTypeAvailable = Boolean(epicIssueType);
		if (epicIssueType) {
			epicIssueTypeName = epicIssueType.name;
		}
	}

	return {
		cards,
		isEpicIssueTypeAvailable,
		epicIssueType: {
			id: epicIssueType?.id ?? 0,
			name: epicIssueTypeName,
			hasRequiredFields: epicIssueType?.hasRequiredFields ?? false,
		},
		epicIssueTypeName,
		...(data?.jira && { timeTrackingOptions: data.jira.timeTrackingOptions }),
		isRankingEnabled: board?.rankCustomFieldId
			? board.rankCustomFieldId !== RANK_DISABLED_ID
			: true,
	};
};

export const getIssueParents = (cards: EpicCardType[]): IssueParent[] =>
	cards.map(({ epic: issueParent }) => {
		return {
			id: String(issueParent.id),
			key: issueParent.key,
			summary: issueParent.name,
			issueType: {
				id: String(issueParent?.issueType?.id) || '',
				name: issueParent?.issueType?.name || '',
				iconUrl: issueParent?.issueType?.iconUrl || '',
			},
			color: issueParent.color || COLORS[0],
		};
	});
